import { AcceptTermsModal } from '@/components/AcceptTermsModal'
import { RouteAnnouncer } from '@/components/RouteAnnouncer'
import { SkipNavigation } from '@/components/SkipNavigation'
import { getLatestByContentType } from '@/services/contentfulClient'
import {
  setPrivacyPolicy,
  setTermsAndConditions,
  setWhatsNew,
} from '@/store/slices/contentfulSlice'
import { useAppDispatch } from '@/store/store'
import AppTheme from '@/themes/AppTheme'
import {
  getCachedContentful,
  setCachedContentful,
} from '@/utils/contentfulCache'
import { IPublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { CssBaseline } from '@mui/material'
import queryString from 'query-string'
import { useEffect } from 'react'
import CookieBot from 'react-cookiebot'
import { BrowserRouter as Router } from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import { ColorSchemeProvider } from './contexts/colorScheme/colorSchemeProvider'
import Routing from './routes/Routing'

const contentTypes = [
  { key: 'whatsNew', set: setWhatsNew },
  { key: 'privacyPolicy', set: setPrivacyPolicy },
  { key: 'termsAndConditions', set: setTermsAndConditions },
]

const App = ({
  disableCustomTheme,
  instance,
}: {
  disableCustomTheme?: boolean
  instance: IPublicClientApplication
}) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    contentTypes.forEach(async ({ key, set }) => {
      const cached = getCachedContentful(key)
      const latest = await getLatestByContentType(key)
      if (!latest) {
        if (cached) {
          dispatch(set(cached))
        }
        return
      }
      const merged =
        cached && cached.updatedDate === latest.updatedDate
          ? { ...latest, hasSeen: cached.hasSeen }
          : { ...latest, hasSeen: false }
      setCachedContentful(key, merged)
      dispatch(set(merged))
    })
  }, [dispatch])

  const cookieBotId = import.meta.env.VITE_COOKIEBOT_ID

  return (
    <MsalProvider instance={instance}>
      <Router>
        <QueryParamProvider
          adapter={ReactRouter6Adapter}
          options={{
            searchStringToObject: queryString.parse,
            objectToSearchString: queryString.stringify,
          }}
        >
          <ColorSchemeProvider>
            <AppTheme disableCustomTheme={disableCustomTheme}>
              {cookieBotId ? <CookieBot domainGroupId={cookieBotId} /> : null}
              <CssBaseline enableColorScheme />
              <SkipNavigation />
              <RouteAnnouncer>
                <Routing />
              </RouteAnnouncer>
              <AcceptTermsModal />
            </AppTheme>
          </ColorSchemeProvider>
        </QueryParamProvider>
      </Router>
    </MsalProvider>
  )
}

export default App
